/* ── StorefrontCustomization.tsx ───────────────────────────────────────── */

import React, { useCallback, useState } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  Pressable,
  TextInput,
  ActivityIndicator,
  Alert,
} from "react-native";
import { Image } from "expo-image";
import { Feather } from "@expo/vector-icons";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import * as ImagePicker from "expo-image-picker";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { uploadStoreImage } from "../utils/supabase/storeImageService";
import { useColors, ThemeColors } from "../hooks/useColors";
import { Vendor } from "@/types/vendor";


type StoreFormValues = {
  storeName: string;
  description?: string;
  bannerImage: string;
  logoImage: string;
};

const MAX_STORE_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;

const schema: yup.ObjectSchema<StoreFormValues> = yup.object({
  storeName: yup.string()
    .required("Store name is required")
    .max(MAX_STORE_NAME_LENGTH, `Store name must be ${MAX_STORE_NAME_LENGTH} characters or less`),
  description: yup.string()
    .optional()
    .max(MAX_DESCRIPTION_LENGTH, `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`),
  bannerImage: yup.string().optional(),
  logoImage: yup.string().optional(),
});

export function StorefrontCustomization({ vendor }: { vendor: Vendor }) {
  const colors = useColors();
  const styles = createStyles(colors);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isDirty, isSubmitting, errors },
  } = useForm<StoreFormValues>({
    defaultValues: {
      storeName: vendor.store_name || "",
      description: vendor.description || "",
      bannerImage: vendor.banner_image || "",
      logoImage: vendor.logo_image || "",
    },
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  const queryClient = useQueryClient();
  const updateVendor = useMutation({
    mutationFn: async (data: any) => {
      console.log("[StorefrontCustomization] updateVendor mutation starting with data:", data);
      const { error } = await supabase
        .from("vendors")
        .update({
          store_name: data.storeName,
          description: data.description,
          banner_image: data.bannerImage,
          logo_image: data.logoImage,
        })
        .eq("id", vendor.id);
      if (error) {
        console.log("[StorefrontCustomization] updateVendor mutation error:", error);
        throw error;
      }
      console.log("[StorefrontCustomization] updateVendor mutation success (no error)");
    },
    onSuccess: (data) => {
      console.log("[StorefrontCustomization] updateVendor mutation onSuccess, data:", data);
      queryClient.invalidateQueries({ queryKey: ["vendor-profile"] });
    },
    onError: (error) => {
      console.log("[StorefrontCustomization] updateVendor mutation onError:", error);
    }
  });

  const [uploading, setUploading] = useState({ banner: false, logo: false });

  const pickImage = useCallback(
    async (type: "banner" | "logo") => {
      try {
        console.log(`[StorefrontCustomization] pickImage: Launching ImagePicker for type "${type}"`);
        const res = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ['images'],
          allowsEditing: true,
          aspect: type === "banner" ? [16, 9] : [1, 1],
          quality: 0.8,
        });
        console.log("[StorefrontCustomization] pickImage: ImagePicker response:", res);
        if (res.canceled) {
          console.log("[StorefrontCustomization] pickImage: Picker canceled by user.");
          return;
        }
        const uri = res.assets[0].uri;
        console.log(`[StorefrontCustomization] pickImage: User selected uri: ${uri}`);
        setUploading((u) => ({ ...u, [type]: true }));
        console.log(`[StorefrontCustomization] pickImage: Starting upload for type "${type}"...`);
        const publicUrl = await uploadStoreImage(uri, vendor.id, type);
        console.log(`[StorefrontCustomization] pickImage: Upload complete. publicUrl: ${publicUrl}`);
        setUploading((u) => ({ ...u, [type]: false }));
        setValue(type === "banner" ? "bannerImage" : "logoImage", publicUrl, {
          shouldDirty: true,
        });
      } catch (err) {
        setUploading((u) => ({ ...u, [type]: false }));
        console.log("[StorefrontCustomization] pickImage: Error occurred:", err);
        Alert.alert("Upload failed", "Try again later.");
      }
    },
    [vendor.id, setValue]
  );

  const onSubmit = handleSubmit(async (data) => {
    console.log("[StorefrontCustomization] onSubmit: Form submission started with data:", data);
    try {
      await updateVendor.mutateAsync(data);
      // Reset form with current values to mark as "clean" (isDirty = false)
      reset(data);
      Alert.alert("Saved", "Your store settings have been updated.");
      console.log("[StorefrontCustomization] onSubmit: Form submission succeeded.");
    } catch (err) {
      console.log("[StorefrontCustomization] onSubmit: Error during form submission:", err);
      Alert.alert("Error", "Could not save changes.");
    }
    console.log("[StorefrontCustomization] onSubmit: Form submission ended.");
  });

  // Logging watched images on render
  console.log("[StorefrontCustomization] render: bannerImage =", watch("bannerImage"), "logoImage =", watch("logoImage"));

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scroll}>
        <SectionCard title="Banner" colors={colors}>
          <ImprovedImageSelector
            uri={watch("bannerImage")}
            placeholder="Tap to add banner"
            uploading={uploading.banner}
            onPress={() => pickImage("banner")}
            colors={colors}
          />
        </SectionCard>

        <SectionCard title="Logo" colors={colors}>
          <ImprovedImageSelector
            uri={watch("logoImage")}
            placeholder="Tap to add logo"
            size={100}
            circle
            uploading={uploading.logo}
            onPress={() => pickImage("logo")}
            colors={colors}
          />
        </SectionCard>

        <SectionCard title="Store Details" colors={colors}>
          <Controller
            control={control}
            name="storeName"
            render={({ field: { onChange, onBlur, value } }) => (
              <FormField
                label="Store Name"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={errors.storeName?.message}
                maxLength={MAX_STORE_NAME_LENGTH}
              />
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field: { onChange, onBlur, value } }) => (
              <FormField
                label="Description"
                value={value || ''}
                onChangeText={onChange}
                onBlur={onBlur}
                multiline
                numberOfLines={3}
                error={errors.description?.message}
                maxLength={MAX_DESCRIPTION_LENGTH}
              />
            )}
          />
        </SectionCard>
      </ScrollView>

      <PrimaryButton
        title={isSubmitting ? "Saving..." : "Save Changes"}
        onPress={onSubmit}
        disabled={!isDirty || isSubmitting}
      />
    </View>
  );
}

const SectionCard = ({
  title,
  children,
  colors,
}: {
  title: string;
  children: React.ReactNode;
  colors: ThemeColors;
}) => {
  const cardStyles = StyleSheet.create({
    card: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      shadowColor: colors.shadow,
      shadowOpacity: 0.1,
      shadowRadius: 6,
      elevation: 3,
      borderWidth: 1,
      borderColor: colors.border,
    },
    cardTitle: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: 12,
      color: colors.text,
    },
  });

  return (
    <View style={cardStyles.card}>
      <Text style={cardStyles.cardTitle}>{title}</Text>
      {children}
    </View>
  );
};

const ImprovedImageSelector = ({
  uri,
  placeholder,
  uploading = false,
  onPress,
  size,
  circle = false,
  colors,
}: {
  uri: string;
  placeholder: string;
  uploading?: boolean;
  onPress: () => void;
  size?: number;
  circle?: boolean;
  colors: ThemeColors;
}) => {
  const dim = size
    ? { width: size, height: size, borderRadius: circle ? size / 2 : 8 }
    : { height: 180 };

  const imageStyles = StyleSheet.create({
    imgBox: {
      backgroundColor: colors.backgroundSecondary,
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
      borderRadius: 8,
    },
    imgPlaceholder: {
      justifyContent: "center",
      alignItems: "center",
    },
    imgText: {
      marginTop: 8,
      color: colors.textLight,
      textAlign: "center",
    },
  });

  return (
    <Pressable style={[imageStyles.imgBox, dim]} onPress={onPress}>
      {uploading ? (
        <ActivityIndicator size="large" color={colors.primary} />
      ) : uri ? (
        <Image 
          source={{ uri }} 
          style={StyleSheet.absoluteFillObject}
          contentFit="cover"
          cachePolicy="memory-disk"
          transition={0}
        />
      ) : (
        <View style={imageStyles.imgPlaceholder}>
          <Feather name="image" size={32} color={colors.textLight} />
          <Text style={imageStyles.imgText}>{placeholder}</Text>
        </View>
      )}
    </Pressable>
  );
};

const FormField = ({
  label,
  error,
  style,
  value,
  onChangeText,
  onBlur,
  multiline,
  numberOfLines,
  maxLength,
}: {
  label: string;
  error?: string;
  style?: any;
  value: string;
  onChangeText: (text: string) => void;
  onBlur: () => void;
  multiline?: boolean;
  numberOfLines?: number;
  maxLength?: number;
}) => {
  const colors = useColors();
  const styles = createStyles(colors);
  return (
    <View style={styles.field}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={styles.label}>{label}</Text>
        {maxLength && (
          <Text style={{ fontSize: 12, color: colors.textLight }}>
            {value?.length || 0}/{maxLength}
          </Text>
        )}
      </View>
      <TextInput
        style={[styles.input, error && styles.inputError, style]}
        placeholder={`Enter ${label.toLowerCase()}`}
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        multiline={multiline}
        numberOfLines={numberOfLines}
        maxLength={maxLength}
      />
      {error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
};

const PrimaryButton = ({
  title,
  onPress,
  disabled,
}: {
  title: string;
  onPress: () => void;
  disabled?: boolean;
}) => {
  const colors = useColors();
  const styles = createStyles(colors);
  return (
    <TouchableOpacity
      style={[styles.button, disabled && { opacity: 0.6 }]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
    >
      <Text style={styles.buttonText}>{title}</Text>
    </TouchableOpacity>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scroll: {
      padding: 16,
      paddingBottom: 80,
    },
    field: {
      marginBottom: 16,
    },
    label: {
      fontWeight: "500",
      marginBottom: 6,
      color: colors.text,
    },
    input: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      color: colors.text,
    },
    inputError: {
      borderColor: colors.error,
    },
    error: {
      color: colors.error,
      marginTop: 4,
      fontSize: 12,
    },
    button: {
      position: "absolute",
      bottom: 16,
      left: 16,
      right: 16,
      backgroundColor: colors.primary,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
    },
    buttonText: {
      color: colors.surface,
      fontSize: 17,
      fontWeight: "600",
    },
  });
